import * as React from 'react';
import { FieldWrapper } from '@progress/kendo-react-form';
import BaseDateRangePicker from '../inputs/datePickers/BaseDateRangePicker';
import { Label, Error, Hint } from '@progress/kendo-react-labels';
// eslint-disable-next-line no-unused-vars
import BaseFormControl from './BaseFormControl';

/**
 * A date range picker form field.
 *
 * @typedef {React.ComponentProps<BaseFormControl>} BaseFieldRenderProps
 * @typedef {React.ComponentPropsWithoutRef<BaseDateRangePicker>} BaseDateRangePickerProps
 *
 * @param {BaseFieldRenderProps & BaseDateRangePickerProps} fieldRenderProps
 * @returns {React.ReactElement} Date range picker form field.
 */
const FormDateRangePicker = (fieldRenderProps) => {
  const {
    validationMessage, touched, label, id, name, valid, disabled, hint, wrapperStyle, readOnly, ...others
  } = fieldRenderProps;

  const editorId = id ?? name;

  const editorRef = React.useRef(null);
  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${editorId}_hint` : '';
  const errorId = showValidationMessage ? `${editorId}_error` : '';
  const labelId = label ? `${editorId}_label` : '';

  return (
    <FieldWrapper style={wrapperStyle}>
      {label && (
        <Label
          id={labelId}
          editorRef={editorRef}
          editorId={editorId}
          editorValid={valid}
          editorDisabled={disabled}
          {...(readOnly ? { className: 'label-readonly' } : {})}
        >
          {label}
        </Label>
      )}
      <BaseDateRangePicker
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ref={editorRef}
        valid={valid}
        id={editorId}
        name={name}
        disabled={disabled}
        readOnly={readOnly}
        dataTestId={editorId}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  );
};

export default FormDateRangePicker;
